import App from 'App';
import ReactDOM from 'react-dom/client';
import initInterceptors from 'services/transportInterceptor';
import { logout } from 'components/auth/auth';

initInterceptors();

// Intercept native fetch 401s globally
const originalFetch = window.fetch;
window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    if (response.status === 401) {
        logout();
    }
    return response;
};

// If a lazy-loaded chunk fails to fetch (404 after redeploy), clear auth and go to login.
window.addEventListener('unhandledrejection', function(e) {
    var reason = e.reason;
    if (
        reason &&
        (reason.name === 'ChunkLoadError' ||
            (typeof reason.message === 'string' && reason.message.includes('Loading chunk')))
    ) {
        try { localStorage.clear(); } catch (err) {}
        window.location.replace('/login');
    }
});
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { RecoilRoot } from 'recoil';
import { ThemeProvider } from 'styled-components';
import { theme } from 'theme';
import { AuthProvider, useAuth } from "./context/AuthContext.js";
import React, { useEffect, useState } from 'react';

import GlobalStyle from './GlobalStyle';
import LanguageProvider from './i18n';
import './index.css';
import reportWebVitals from './reportWebVitals';
import supabaseHandler from 'utils/SupaBase';

const Root = () => {
    const [locale, setLocale] = useState('en');

    useEffect(() => {
        let helpCrunchInitialized = false;
        let checkAuthInterval;

        // On reload: verify user still exists in DB; if not, clear and redirect
        const validateUserOnReload = async () => {
            try {
                const raw = localStorage.getItem('user');
                if (!raw) return;
                const parsed = JSON.parse(raw);
                const email = parsed?.user?.email;
                if (!email) return;

                // Get JWT token for authorization
                // Note: This is in index.js outside of AuthProvider, so we need to use getSession here
                const { data: tokenData } = await supabaseHandler.auth.getSession();
                const token = tokenData?.session?.access_token;

                if (!token) {
                    return;
                }

                // Fetch profile from backend API
                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/validate-user`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ email: String(email).toLowerCase() })
                    }
                );

                const result = await response.json();

                // Backend returns { data, error } format
                // Do not redirect to login when user is on the public payment page (email link)
                const isPaymentPage = /^\/payment\/[a-f0-9-]+$/i.test(window.location.pathname);
                if (!response.ok || result.error || !result.data) {
                    if (!isPaymentPage) {
                        localStorage.clear();
                        window.location.replace('/login');
                    }
                }
            } catch (e) {
                // If parsing fails or request errors, do not force logout
            }
        };

        validateUserOnReload();

        // Function to check if user is authenticated using localStorage
        const isUserAuthenticated = () => {
            for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.endsWith('auth-token')) {
                const token = localStorage.getItem(key);
                if (token) return true;
            }
            }
            return false; // No matching token found
        };
  

        // Function to initialize HelpCrunch
        const initializeHelpCrunch = () => {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.innerHTML = `
                window.helpcrunchSettings = {
                    organization: 'beseenhealth',
                    appId: 'b391eb58-8707-4fba-b73c-856a9975234f',
                };
                (function(w, d) {
                    var hS = w.helpcrunchSettings;
                    if (!hS || !hS.organization) return;
                    var widgetSrc = 'https://embed.helpcrunch.com/sdk.js';
                    w.HelpCrunch = function() { w.HelpCrunch.q.push(arguments) };
                    w.HelpCrunch.q = [];
                    function r() {
                        if (d.querySelector('script[src="' + widgetSrc + '"]')) return;
                        var s = d.createElement('script');
                        s.async = 1;
                        s.type = 'text/javascript';
                        s.src = widgetSrc;
                        (d.body || d.head).appendChild(s);
                    }
                    if (d.readyState === 'complete' || hS.loadImmediately) r();
                    else if (w.attachEvent) w.attachEvent('onload', r);
                    else w.addEventListener('load', r, false);
                })(window, document);
            `;
            document.body.appendChild(script);
            helpCrunchInitialized = true;
        };

        // Function to update HelpCrunch visibility
        const updateHelpCrunchVisibility = () => {
            if (!helpCrunchInitialized) {
                return;
            }

            if (window.HelpCrunch) {
                // Don't update visibility if a modal is currently open
                if (window.helpcrunchModalOpen) {
                    return;
                }
                
                // Check if the widget was manually opened by the user from the Contact Us menu
                if (window.helpcrunchManuallyOpened) {
                    // Don't hide the widget if it was manually opened
                    return;
                }
                
                // Always show the widget button by default (just the button, not opening the chat)
                window.HelpCrunch('showChatWidget');
            }
        };
        
        // Expose the function globally so FormModal can call it
        window.updateHelpCrunchVisibility = updateHelpCrunchVisibility;

        // Initialize HelpCrunch
        initializeHelpCrunch();

        // Check authentication status and update widget visibility
        const checkAuthAndUpdateVisibility = () => {
            updateHelpCrunchVisibility();
            
            // Set up interval to periodically check if HelpCrunch is available
            if (!checkAuthInterval) {
                checkAuthInterval = setInterval(() => {
                    if (window.HelpCrunch) {
                        updateHelpCrunchVisibility();
                    }
                }, 1000);
            }
        };

        // Initial check
        checkAuthAndUpdateVisibility();

        // Listen for storage events (login/logout)
        const handleStorageChange = (e) => {
            if (e.key && e.key.endsWith('auth-token')) {
              updateHelpCrunchVisibility();
            }
          };
        
        window.addEventListener('storage', handleStorageChange);

        // Clean up
        return () => {
            if (checkAuthInterval) {
                clearInterval(checkAuthInterval);
            }
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <GlobalStyle />
            <LanguageProvider locale={locale}>
                {/*<React.StrictMode>*/}
                <RecoilRoot>
                    <BrowserRouter>
                    <AuthProvider>
                        <div onClick={() => toast.dismiss()} style={{ display: 'contents' }}>
                        <App />
                        <ToastContainer autoClose={3000} />
                        </div>
                        </AuthProvider>
                    </BrowserRouter>
                </RecoilRoot>
                {/*</React.StrictMode>*/}
            </LanguageProvider>
        </ThemeProvider>
    );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<Root />);

// If you want to start measuring performance in your app, pass a function
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
